import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Container, Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import LoadingButton from '~/components/LoadingButton';
import boraImpactarLogo from '~/images/illustrations/bora-impactar.svg';
import { useAuth } from '~/single-page/hooks/useAuth';
import { APP_ROUTES } from '~/single-page/constants/routes';
import GoBackButton from '~/components/GoBackButton';
import 'react-toastify/dist/ReactToastify.css';

export default function SignIn() {
  const { signIn, loading, errors } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsValidEmail(newEmail === '' || validateEmail(newEmail));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    document.activeElement.blur();
    toast.dismiss();

    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    await signIn(email, password);
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#CBEFFF',
        position: 'relative',
        pt: { xs: 8, sm: 8, md: 10 },
        pb: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <ToastContainer autoClose={8000} />
      <Box sx={{ 
        position: 'absolute', 
        top: { xs: 16, sm: 24 }, 
        left: { xs: 16, sm: 24 }
      }}>
        <GoBackButton />
      </Box>
      
      <Container 
        maxWidth="sm" 
        sx={{ 
          p: 0,
          height: '100%',
          display: 'flex',
          mt: { xs: 2, sm: 0 },
          alignItems: { xs: 'flex-start', sm: 'center' }
        }}
      >
        <Fade in timeout={800}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              padding: { xs: 2.5, sm: 3, md: 3.5 },
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)'
              }
            }}
          >
            <img 
              src={boraImpactarLogo} 
              alt="Bora Impactar" 
              style={{
                width: '160px',
                maxWidth: '100%',
                height: 'auto',
                marginBottom: '20px'
              }}
            />
            
            <Typography 
              variant="h4" 
              sx={{ 
                mb: { xs: 3, sm: 3, md: 2.5 }, 
                fontWeight: 700, 
                textAlign: 'center',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                px: { xs: 1, sm: 0 }
              }}
            >
              Entrar
            </Typography>

            <form onSubmit={handleSubmit} noValidate style={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                label="Email"
                value={email}
                error={!!errors.email || !isValidEmail}
                helperText={errors.email || (!isValidEmail && 'Email inválido')}
                onChange={handleEmailChange}
                autoComplete="email"
                autoFocus
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: { xs: '48px', sm: '56px' },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                label="Senha"
                value={password}
                error={!!errors.password}
                helperText={errors.password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                autoComplete="current-password"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: { xs: '48px', sm: '56px' },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              
              {/* <Box sx={{ 
                mt: { xs: 1.5, sm: 1.5 }, 
                textAlign: 'center' 
              }}>
                <Box
                  component="a"
                  href={APP_ROUTES.REQUEST_PASSWORD_RESET}
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'primary.dark',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Esqueceu a senha?
                </Box>
              </Box> */}

              <LoadingButton
                type="submit"
                fullWidth
                loading={loading}
                variant="contained"
                size="large"
                color="primary"
                sx={{
                  mt: { xs: 3, sm: 3, md: 2.5 },
                  mb: { xs: 1.5, sm: 1.5 },
                  py: { xs: 1.25, sm: 1.5, md: 1.25 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  borderRadius: { xs: 1.5, sm: 2 },
                  background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #4F97FF 0%, #294BB6 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(41, 75, 182, 0.25)'
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
              >
                Entrar
              </LoadingButton>
            </form>

            <Box 
              sx={{ 
                mt: { xs: 3, sm: 3, md: 2.5 }, 
                pt: { xs: 3, sm: 3, md: 2.5 }, 
                textAlign: 'center', 
                width: '100%',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: { xs: 1.5, sm: 2 },
                  color: 'text.secondary',
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}
              >
                Você representa uma ONG?
              </Typography>
              
              <Button 
                fullWidth 
                variant="outlined" 
                component="a" 
                size="large" 
                color="primary" 
                href={APP_ROUTES.REGISTER}
                sx={{ 
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  borderWidth: 2,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(41, 75, 182, 0.15)'
                  }
                }}
              >
                Cadastre sua ONG
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}
